const API_BASE = process.env.REACT_APP_API_BASE || "";
export const AuthAPI = {
    login: async ({ id, password }) => {
    // dev에선 프록시로, 배포에선 같은 출처로 호출되게 상대경로 사용
    const res = await fetch(API_BASE+'/auth/login', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, password }),
      credentials: "include",
    });

    const ct = res.headers.get("content-type") || "";
    const isJson = ct.includes("application/json");
    const data = isJson ? await res.json() : null;

    if (!res.ok) {
      const msg = isJson ? data?.error?.message || JSON.stringify(data) : (await res.text().catch(() => "")) || `HTTP ${res.status}`;
      throw new Error(msg);
    }

    // 기대 형태: { success: true, data: { token, user } }
    if (!data?.success || !data?.data?.token) {
      throw new Error(data?.error?.message || "로그인 응답 형식이 예상과 다릅니다.");
    }
    return data.data; // { token, user }
  },

  register: async (payload) => {
    // { branch_idx, id, user_name, password, phone_number, email }
    const res = await fetch(API_BASE+"/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error?.message || `HTTP ${res.status}`);
    if (!data?.success) throw new Error(data?.error?.message || "회원가입 실패");
    return true;
  },

  // 비밀번호 찾기 1단계: id/phone_number/email 확인 후 reset_token 발급
  // (서버에서 phone_number의 '-'는 자동 제거 후 비교)
  verifyForgotPassword: async ({ id, phone_number, email }) => {
    const res = await fetch(API_BASE + "/auth/verify-forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ id, phone_number, email }),
    });
    const ct = res.headers.get("content-type") || "";
    const isJson = ct.includes("application/json");
    const data = isJson ? await res.json().catch(() => null) : null;
    if (!res.ok) {
      const msg = data?.message || data?.error?.message || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    const resetToken = data?.data?.reset_token;
    if (!data?.success || !resetToken) {
      throw new Error(data?.message || data?.error?.message || "사용자 확인에 실패했습니다.");
    }
    return resetToken;
  },

  // 비밀번호 찾기 2단계: reset_token + 새 비밀번호로 변경
  resetPassword: async ({ reset_token, new_password }) => {
    const res = await fetch(API_BASE + "/auth/reset-password", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ reset_token, new_password }),
    });
    const ct = res.headers.get("content-type") || "";
    const isJson = ct.includes("application/json");
    const data = isJson ? await res.json().catch(() => null) : null;
    if (!res.ok) {
      const msg = data?.message || data?.error?.message || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    if (!data?.success) {
      throw new Error(data?.message || data?.error?.message || "비밀번호 변경에 실패했습니다.");
    }
    return true;
  },
};

function joinURL(base, path) {
  if (/^https?:\/\//i.test(path)) return path;          // 이미 절대경로면 그대로
  const b = (base || "").replace(/\/+$/, "");           // base 끝 슬래시 제거
  const p = path.startsWith("/") ? path : `/${path}`;   // path 앞에 슬래시 보장
  return `${b}${p}`;
}

async function fetchJSONOnce(url) {
  const res = await fetch(url, { method: "GET" });
  const ct = res.headers.get("content-type") || "";
  const raw = await res.text(); // 바디는 한 번만 읽기

  if (!res.ok) {
    return { ok: false, reason: `HTTP ${res.status}${raw ? ` - ${raw.slice(0,120)}` : ""}` };
  }
  if (!ct.includes("application/json")) {
    // HTML이 오면 즉시 실패로 간주
    return { ok: false, reason: "non-JSON response" };
  }
  try {
    const data = raw ? JSON.parse(raw) : null;
    return { ok: true, data };
  } catch {
    return { ok: false, reason: `JSON parse fail: ${raw.slice(0,120)}` };
  }
}

export const BranchAPI = {
  getList: async ({ status = 1, area_sort } = {}) => {
    // 언어 분기: zh-CN이면 9, 그 외 -1 (기존 jQuery 동일 로직)
    const lang = (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
    const sort = typeof area_sort === "number" ? area_sort : (lang === "zh-CN" ? 9 : -1);
    const qs = new URLSearchParams({ status, area_sort: sort }).toString();
    const path = `/api/getBranch?${qs}`;
    
    // ✅ 1) 우선순위대로 시도: window.API_BASE_URL → .env(REACT_APP_API_BASE) → 배포도메인 → 상대경로
    const candidates = [];
      candidates.push(joinURL(API_BASE, path));
    // if (API_BASE) candidates.push(joinURL(API_BASE, path));

    candidates.push(API_BASE+"/api/getBranch?" + qs); // 안전 폴백
    candidates.push(path); // 최후의 수단(프록시 구성 시)

    let lastErr = "unknown";
    for (const url of candidates) {
      const r = await fetchJSONOnce(url);
      if (r.ok) {
        // 관대 파싱: 여러 키 형태 허용
        const data = r.data;
        let list = null;
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data?.data)) list = data.data;
        else if (Array.isArray(data?.list)) list = data.list;
        else if (Array.isArray(data?.rows)) list = data.rows;
        const okFlag = data?.success === true || data?.success === "true" || data?.success === 1 || data?.ok === true;
        if (!list && okFlag && Array.isArray(data?.data)) list = data.data;

        if (Array.isArray(list)) return list;
        lastErr = "예상 형식 아님";
        // 다음 후보 URL로 계속 재시도
      } else {
        lastErr = r.reason || "request failed";
      }
    }
    throw new Error(`지점 리스트 로드 실패: ${lastErr}`);
  },
};

export const ProfileAPI = {
  updateUser: async (payload) => {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    const path = "/api/updateUser";

    // BranchAPI와 동일한 후보 URL 우선순위
    const candidates = [];
      candidates.push(joinURL(API_BASE, path));
    // if (API_BASE) candidates.push(joinURL(API_BASE, path));
    candidates.push(API_BASE+"/api/updateUser"); // 배포 도메인 폴백
    candidates.push(path); // 최후의 수단(프록시 구성 시)

    let lastErr = "unknown";
    for (const url of candidates) {
      try {
        const res = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });
        const raw = await res.text();
        let data = null;
        try { data = raw ? JSON.parse(raw) : null; } catch { /* JSON 아닐 수 있음 */ }

        if (!res.ok) {
          lastErr = data?.error?.message || `HTTP ${res.status} ${raw ? `- ${raw.slice(0,120)}` : ""}`;
          continue; // 다음 후보 URL 시도
        }
        if (!data?.success) {
          lastErr = data?.error?.message || "회원정보 수정 실패";
          continue;
        }

        // 성공
        return data?.data || null;
      } catch (e) {
        lastErr = e?.message || "request failed";
        // 다음 후보 URL 시도
      }
    }
    throw new Error(lastErr);
  },
};